import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Award, BookOpen, Users, Star } from 'lucide-react';
import { SectionTitle, GoldDivider, FlowerMotif } from './premium/Decor';

/**
 * About our astrologer section — Astro Avinash brand identity.
 * Mirrors the "About Astro Arun Pandit" section of reference site
 * with our own astrologer persona + "Featured In" niche-media strip
 * (fictional spiritual outlets — no real news-channel claims).
 */
const PRESS = [
  'AstroDrishti',
  'Jyotish Sandesh',
  'Karma Voice',
  'Sanatan Tribune',
  'Cosmic Bharat',
  'Aastha Darshan',
  'Vedic Pulse',
  'Vaani Today',
];

const STATS = [
  { icon: Users, num: '10,000+', hi: 'खुश ग्राहक', en: 'Happy Clients' },
  { icon: BookOpen, num: '15+', hi: 'वर्ष का अनुभव', en: 'Years Experience' },
  { icon: Award, num: '99%', hi: 'सटीकता', en: 'Accuracy Rate' },
  { icon: Star, num: '4.9/5', hi: 'औसत रेटिंग', en: 'Avg. Rating' },
];

const AboutAstrologerSection = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="about-astrologer-section"
      className="relative bg-white py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-mandala-large bg-no-repeat bg-center opacity-30 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          {/* LEFT: portrait — no square frame, radial-mask soft blend into section */}
          <div className="lg:col-span-2 relative">
            <div className="relative max-w-md mx-auto">
              {/* Warm gold radial glow behind portrait — merges with cream/white bg */}
              <div
                className="absolute -inset-8 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.10) 40%, transparent 70%)',
                  filter: 'blur(28px)',
                }}
              />

              {/* Portrait image — soft radial mask so edges dissolve into background, no hard border */}
              <div className="relative aspect-[4/5]">
                <img
                  src="/generated/astro-avinash.webp"
                  alt={isHi ? 'एस्ट्रो अविनाश — मुख्य ज्योतिषाचार्य' : 'Astro Avinash — Chief Astrologer'}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    WebkitMaskImage:
                      'radial-gradient(ellipse 68% 78% at 50% 45%, #000 55%, rgba(0,0,0,0.85) 78%, transparent 100%)',
                    maskImage:
                      'radial-gradient(ellipse 68% 78% at 50% 45%, #000 55%, rgba(0,0,0,0.85) 78%, transparent 100%)',
                    filter: 'drop-shadow(0 30px 50px rgba(74,9,18,0.35))',
                  }}
                />

                {/* Floating "Certified Astrologer" pill — top center */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-brand-maroon-dark/85 backdrop-blur-md border border-brand-gold/50 rounded-full px-4 py-1.5 shadow-lg">
                  <FlowerMotif className="w-3 h-3 text-brand-gold-light" />
                  <span className={`text-[10px] uppercase tracking-[0.25em] text-brand-gold-light font-bold ${isHi ? 'font-hindi' : ''}`}>
                    {isHi ? 'प्रमाणित ज्योतिषाचार्य' : 'Certified Astrologer'}
                  </span>
                  <FlowerMotif className="w-3 h-3 text-brand-gold-light" />
                </div>

                {/* Name — sits below the fading portrait bottom, no hard bg strip */}
                <div className="absolute inset-x-0 bottom-2 z-30 text-center px-4">
                  <p className={`text-brand-gold-dark text-[10px] uppercase tracking-[0.35em] mb-0.5 font-bold ${isHi ? 'font-hindi' : ''}`}>
                    {isHi ? 'मुख्य ज्योतिषाचार्य' : 'Chief Astrologer'}
                  </p>
                  <p className={`text-brand-maroon-dark font-playfair font-black text-2xl md:text-3xl leading-tight drop-shadow-sm ${isHi ? 'font-hindi-serif' : ''}`}>
                    {isHi ? 'एस्ट्रो अविनाश' : 'Astro Avinash'}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-1.5">
                    <span className="w-8 h-px bg-brand-gold-dark/60" />
                    <span className="text-brand-gold-dark text-[10px] uppercase tracking-[0.3em] font-bold">
                      Karma Kundali
                    </span>
                    <span className="w-8 h-px bg-brand-gold-dark/60" />
                  </div>
                </div>
              </div>

              {/* Floating "15+ years" badge — repositioned to not overlap the faded edge */}
              <div className="absolute top-6 -right-2 md:right-2 bg-brand-gold text-brand-maroon-dark rounded-full w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center shadow-premium border-[3px] md:border-4 border-white rotate-[-8deg] z-40">
                <Award className="w-4 h-4 md:w-5 md:h-5" />
                <span className="font-playfair font-bold text-lg md:text-xl leading-none">15+</span>
                <span className={`text-[8px] md:text-[9px] uppercase tracking-wider font-bold mt-0.5 ${isHi ? 'font-hindi' : ''}`}>
                  {isHi ? 'वर्ष' : 'Years'}
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT: copy */}
          <div className="lg:col-span-3 text-center lg:text-left">
            <p className="text-brand-gold-dark uppercase tracking-[0.25em] text-xs sm:text-sm font-semibold mb-3">
              {isHi ? 'हमारे बारे में' : 'About'}
            </p>
            <h2 className={`font-playfair font-bold text-3xl md:text-4xl lg:text-5xl text-brand-maroon leading-tight mb-3 ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi ? 'एस्ट्रो अविनाश' : 'Astro Avinash'}
            </h2>
            <p className={`text-brand-gold-dark text-base font-semibold mb-5 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi ? 'प्रमाणित वैदिक ज्योतिषाचार्य • स्वर्ण पदक विजेता' : 'Certified Vedic Astrologer • Gold Medalist'}
            </p>
            <GoldDivider className="!my-4 lg:!justify-start" />

            <p className={`text-brand-text-soft text-base md:text-lg leading-relaxed mb-5 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'एस्ट्रो अविनाश जी कर्मा कुंडली के मुख्य ज्योतिषाचार्य हैं और भारत के एक सम्मानित वैदिक ज्योतिषी हैं, जिन्होंने 10,000 से अधिक कुंडलियों का विश्लेषण किया है — सटीक अंतर्दृष्टि और व्यावहारिक मार्गदर्शन से जीवन को प्रभावित किया है।'
                : 'Astro Avinash ji is the chief astrologer at Karma Kundali and one of India\'s most respected Vedic astrologers, having analyzed 10,000+ Kundlis and impacted lives with accurate insights and practical guidance.'}
            </p>
            <p className={`text-brand-text-soft text-base md:text-lg leading-relaxed mb-7 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'पारिवारिक परंपरा से 50+ वर्षों की समृद्ध विरासत के साथ मार्गदर्शन प्राप्त — एस्ट्रो अविनाश जी मानते हैं कि ज्योतिष विश्वास का नहीं, सटीक विज्ञान का विषय है। यह आपके जीवन में स्पष्टता, दिशा और सशक्त निर्णय लेने की अपार क्षमता रखता है।'
                : 'Mentored by a family tradition spanning 50+ years of astrological mastery, Astro Avinash ji believes astrology is not a matter of belief — it is a precise science holding immense potential to provide clarity, direction, and empowered decision-making.'}
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
              {STATS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="flex flex-col items-center lg:items-start gap-1.5 bg-brand-cream rounded-2xl border border-brand-gold/25 p-4">
                    <Icon className="w-5 h-5 text-brand-gold-dark" />
                    <span className="font-playfair font-black text-2xl md:text-3xl text-brand-maroon">{s.num}</span>
                    <span className={`text-xs text-brand-text-mute uppercase tracking-wider font-semibold ${isHi ? 'font-hindi' : ''}`}>
                      {isHi ? s.hi : s.en}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Featured In — niche spiritual media strip */}
        <div className="mt-20 pt-12 border-t border-brand-gold/20">
          <p className={`text-center text-brand-gold-dark uppercase tracking-[0.3em] text-xs sm:text-sm font-semibold mb-8 ${isHi ? 'font-hindi' : ''}`}>
            {isHi ? 'इन ज्योतिष मीडिया में चर्चित' : 'Featured In'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 md:gap-x-14">
            {PRESS.map((logo, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-cream/60 border border-brand-gold/15 hover:border-brand-gold/40 transition-colors grayscale hover:grayscale-0"
              >
                <FlowerMotif className="w-4 h-4 text-brand-maroon/60" />
                <span className="font-playfair font-bold text-base md:text-lg text-brand-text-soft tracking-wide">
                  {logo}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutAstrologerSection;
